import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Check, Zap, XCircle, Paperclip, Send } from "lucide-react";

const DECISIONS = {
  renew:       { label: "Renew",       tone: "emerald", Icon: Check,   hint: "Spawns a linked renewal draft in the intake pipeline." },
  renegotiate: { label: "Renegotiate", tone: "amber",   Icon: Zap,     hint: "Flags this for a fresh round of terms before renewal." },
  let_lapse:   { label: "Let Lapse",   tone: "slate",   Icon: XCircle, hint: "Records the intent to not renew — no draft created." },
};

const TONES = {
  emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
  amber:   "bg-amber-500 hover:bg-amber-600 text-white",
  slate:   "bg-slate-600 hover:bg-slate-700 text-white",
};

/**
 * Rich decision sheet for a renewal item. Captures a rationale note + optional
 * attachment, then optionally auto-notifies the assigned attorney by posting a
 * follow-up note that @-mentions them (reuses the existing bell-notification path).
 */
export default function RenewalDecisionModal({ open, contract, decision, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [notifyAttorney, setNotifyAttorney] = useState(true);
  const [attorneys, setAttorneys] = useState([]);
  const [attorneyId, setAttorneyId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote(""); setFile(null); setNotifyAttorney(true);
    // Pull team once so we can auto-select the assigned attorney; fall back to any attorney if unknown.
    api.get("/users/team").then(r => {
      const list = (r.data || []).filter(u => u.role === "attorney");
      setAttorneys(list);
      const assigned = list.find(u => u.name === contract?.attorney);
      setAttorneyId(assigned?.id || list[0]?.id || "");
    }).catch(() => setAttorneys([]));
  }, [open, contract?.contract_id, contract?.attorney]);

  if (!open || !contract || !decision) return null;
  const d = DECISIONS[decision];
  if (!d) return null;
  const Icon = d.Icon;

  const submit = async () => {
    setBusy(true);
    try {
      // 1) Record the decision (unchanged endpoint, note is short summary).
      const { data } = await api.post(`/renewals/${contract.contract_id}/decide`, {
        decision, note: note.trim(),
      });
      // 2) Optionally post a rich note with attachment + attorney mention so the
      //    assigned attorney gets a bell notification and the note lives on the
      //    contract's notes thread (visible from both /renewals and /contract/:id).
      if ((note.trim() || file) && (notifyAttorney && attorneyId || file)) {
        const fd = new FormData();
        fd.append("body", `[${d.label} decision] ${note.trim()}`);
        fd.append("visibility", "internal");
        if (notifyAttorney && attorneyId) fd.append("mentions", attorneyId);
        if (file) fd.append("file", file);
        try {
          await api.post(`/contracts/${contract.contract_id}/notes`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch { /* non-fatal — decision itself succeeded */ }
      }
      if (data.linked_child_id) toast.success(`Renewal draft created · ${notifyAttorney ? "attorney notified" : "no notification"}`);
      else toast.success(`Recorded: ${d.label}${notifyAttorney ? " · attorney notified" : ""}`);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record decision");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}
         data-testid="renewal-decision-modal">
      <div className="bg-white rounded-md w-[520px] max-w-[92vw] shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-700" />
          <div className="font-bold text-slate-900">{d.label} — #{contract.contract_number}</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
            <div className="font-semibold text-slate-900">{contract.vendor}</div>
            <div className="text-slate-500">{contract.department} · renews {new Date(contract.renewal_date).toLocaleDateString()}</div>
            <div className="mt-1 text-slate-500 italic">{d.hint}</div>
          </div>

          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Rationale note</span>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
                      placeholder="Why this decision? Add context, negotiation targets, key dates…"
                      data-testid="decision-note"
                      className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:border-[#005289] focus:outline-none resize-none" />
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">
              <Paperclip className="w-3 h-3" /> {file ? file.name.slice(0, 24) : "Attach quote / email"}
              <input type="file" className="hidden" onChange={e => setFile(e.target.files[0] || null)} data-testid="decision-file" />
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={notifyAttorney} onChange={e => setNotifyAttorney(e.target.checked)}
                     data-testid="decision-notify-toggle" />
              Notify assigned attorney
            </label>
            {notifyAttorney && attorneys.length > 0 && (
              <select value={attorneyId} onChange={e => setAttorneyId(e.target.value)}
                      data-testid="decision-attorney-select"
                      className="h-7 text-xs border border-slate-200 rounded px-2">
                {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded" data-testid="decision-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="decision-submit"
                  className={`ml-auto h-9 px-4 rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-60 ${TONES[d.tone]}`}>
            <Send className="w-3 h-3" /> {busy ? "Recording…" : `Confirm ${d.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
